import type { TimeOffEntry, TimeOffStatus } from '../../../api/timeOff';

export interface TimeOffOccupancy {
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  status?: TimeOffStatus;
}

export function isActiveTimeOffStatus(status: TimeOffStatus | undefined): boolean {
  return status == null || status === 'Pending' || status === 'Approved';
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function utcDateKey(value: Date): string {
  return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
}

export function localDateKey(value: Date): string {
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

/** Calendar days an entry occupies. All-day uses stored UTC dates; timed uses the operator local zone. */
export function occupiedDateKeys(startUtc: string, endUtc: string, isAllDay: boolean): string[] {
  const start = new Date(startUtc);
  const end = new Date(endUtc);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  if (isAllDay) {
    const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
    let endExclusive = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
    if (endExclusive <= cursor) {
      endExclusive = new Date(cursor);
      endExclusive.setUTCDate(endExclusive.getUTCDate() + 1);
    }
    const keys: string[] = [];
    for (; cursor < endExclusive; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
      keys.push(utcDateKey(cursor));
    }
    return keys;
  }

  let endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  if (end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0 && end.getMilliseconds() === 0) {
    endDate.setDate(endDate.getDate() - 1);
  }
  const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  if (endDate < startDate) endDate = startDate;

  const keys: string[] = [];
  for (const cursor = new Date(startDate); cursor <= endDate; cursor.setDate(cursor.getDate() + 1)) {
    keys.push(localDateKey(cursor));
  }
  return keys;
}

export function occupiedDateSetsOverlap(left: TimeOffOccupancy, right: TimeOffOccupancy): boolean {
  const leftKeys = new Set(occupiedDateKeys(left.startUtc, left.endUtc, left.isAllDay));
  return occupiedDateKeys(right.startUtc, right.endUtc, right.isAllDay).some((key) => leftKeys.has(key));
}

export function findPersistedSameDayConflicts(
  drafts: TimeOffOccupancy[],
  existing: TimeOffEntry[],
  excludeEntryId?: string
): TimeOffEntry[] {
  const activeDrafts = drafts.filter((draft) => isActiveTimeOffStatus(draft.status));
  if (activeDrafts.length === 0) return [];

  const hits: TimeOffEntry[] = [];
  const seen = new Set<string>();
  for (const entry of existing) {
    if (excludeEntryId && entry.id === excludeEntryId) continue;
    if (!isActiveTimeOffStatus(entry.status)) continue;
    const occupancy: TimeOffOccupancy = {
      startUtc: entry.startUtc,
      endUtc: entry.endUtc,
      isAllDay: entry.isAllDay,
      status: entry.status
    };
    if (!activeDrafts.some((draft) => occupiedDateSetsOverlap(draft, occupancy))) continue;
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    hits.push(entry);
  }
  return hits;
}

export function hasIntraDraftSameDayConflict(drafts: TimeOffOccupancy[]): boolean {
  const active = drafts.filter((draft) => isActiveTimeOffStatus(draft.status));
  for (let i = 0; i < active.length; i++) {
    for (let j = 0; j < i; j++) {
      if (occupiedDateSetsOverlap(active[i], active[j])) return true;
    }
  }
  return false;
}
